import {
  BackgroundColor,
  CardBackgroundColor,
  ContentAlign,
  getBackgroundColor,
  TitleSize,
} from '@api/selections';
import { BlockComponentBaseProps, EpiImage, EpiLink, ImageConfig } from '@api/types';
import {
  BackgroundCard,
  BlockContainer,
  EditPanel,
  Html,
  Image,
  SmartLink,
  Title,
} from '@components';
import { Box, styled, Typography } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints, palette } from '@themes/theme.helpers';
import classnames from 'classnames';

// STYLES
//--------
const TeaserBlockWrapper = styled(Box)`
  // Color variants
  // Background colors
  &.bg-black {
    background: ${palette.common('black')};
  }

  &.bg-white {
    background: ${palette.common('white')};
  }

  &.bg-transparent {
    background: transparent;
  }

  // Main content wrapper
  .teaser-content {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    padding: 16px 0;

    ${breakpoints.up('md')} {
      flex-direction: row;
      padding: 60px 0;
    }

    // Left - right variants
    ${breakpoints.up('md')} {
      &.right {
        flex-direction: row-reverse;
      }
    }
  }

  .teaser-card {
    padding: 16px;
    z-index: 100;
    flex-grow: 1;
    margin: -40px 24px 0;
    color: ${palette.text('primary')};
    position: relative;

    // Hover effect
    transition: transform 200ms linear;

    ${breakpoints.up('md')} {
      min-height: 200px;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
    }

    ${breakpoints.up('lg')} {
      min-height: 300px;
    }

    // With Link
    &.with-link:hover {
      transform: scale(1.05);
    }

    &.left {
      ${breakpoints.up('md')} {
        padding: 32px 32px 32px 128px;
        margin: 0 0 0 -80px;
      }
      ${breakpoints.up('lg')} {
        padding: 40px 40px 40px 128px;
        margin: 0 0 0 -80px;
      }
    }
    &.right {
      ${breakpoints.up('md')} {
        padding: 32px;
        margin: 0 -80px 0 0;
      }
      ${breakpoints.up('lg')} {
        padding: 40px 128px 40px 40px;
        margin: 0 -80px 0 0;
      }
    }

    &.quote {
      background: ${colors.secondary.lightBlue};
      &.left {
        padding-bottom: 32px;
        ${breakpoints.up('md')} {
          padding-bottom: 64px;
        }
      }
      &.right {
        padding-top: 32px;
        ${breakpoints.up('md')} {
          padding-top: 64px;
        }
      }
      .description * {
        font-family: 'Lato';
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        font-size: 16px;
        text-align: left;
        ${breakpoints.up('md')} {
          font-size: 24px;
        }
      }
    }
  }
`;

const TeaserImageBox = styled(Box)`
  flex-shrink: 0;
  flex-grow: 0;
  width: 100%;
  ${breakpoints.up('md')} {
    width: 60%;
  }
  img {
    border-radius: 4px;
  }
`;

const AuthorBox = styled(Box)`
  font-family: 'Lato';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  text-align: right !important;
  letter-spacing: 0.01em;
  margin-top: 16px;
  ${breakpoints.up('md')} {
    margin-top: 24px;
  }
`;

const QuoteIcon = styled(Box)`
  background: url(/icons/quote.png) no-repeat center;
  background-size: contain;
  position: absolute;
  width: 40px;
  height: 40px;
  top: -20px;
  left: 20px;
  ${breakpoints.up('md')} {
    width: 93px;
    height: 76px;
    top: -40px;
    left: 40px;
  }

  &.left {
    top: auto;
    left: auto;
    right: 20px;
    bottom: -20px;
    transform: rotateZ(180deg);
    ${breakpoints.up('md')} {
      right: 40px;
      bottom: -40px;
    }
  }
`;

// TYPES
//-------
export type TeaserBlockProps = BlockComponentBaseProps & {
  sectionTitle?: string;
  sectionTitleSize?: TitleSize;
  sectionTitleAlign?: ContentAlign;
  title?: string;
  text?: string;
  image?: EpiImage;
  link?: EpiLink;
  backgroundColor?: BackgroundColor;
  cardBackgroundColor?: CardBackgroundColor;
  imagePlacement?: 'left' | 'right';
  imageConfig?: ImageConfig;
  showAsQuote?: boolean;
  quoteAuthor?: string;
};

// COMPONTENTS
//-------------

export const TeaserBlock = ({
  sectionTitle,
  sectionTitleSize,
  sectionTitleAlign,
  title,
  image,
  text,
  link,
  backgroundColor = 'transparent',
  cardBackgroundColor = 'transparent',
  imagePlacement = 'left',
  imageConfig,
  disableEditPanel = false,
  showAsQuote,
  quoteAuthor,
}: TeaserBlockProps) => {
  const backgroundColorValue = backgroundColor ? backgroundColor : 'transparent';
  const mainClass = classnames('teaser-block', getBackgroundColor(backgroundColorValue));
  const cardClass = classnames('teaser-card', imagePlacement, {
    quote: showAsQuote,
    'with-link': link,
  });
  const contentClass = classnames('teaser-content', imagePlacement);
  const hasContent = title || text;
  const pictureSources = [
    {
      media: '(max-width:600px)',
      params: { width: 400, quality: 60, format: 'webp' } as const,
    },
    {
      media: '(max-width:960px)',
      params: { width: 600, quality: 70, format: 'webp' } as const,
    },
    {
      params: { width: 900, quality: 85, format: 'webp' } as const,
    },
  ];

  const sizes = '(max-width:600px) 100vw, (max-width:960px) 50vw, 600px';
  const mainContent = (
    <Box className={contentClass}>
      <TeaserImageBox className='teaser-image' data-epi-edit='Image'>
        <Image
          image={image}
          {...imageConfig}
          addWrapper
          width='900'
          height='600'
          scalingMode='fill'
          pictureSources={pictureSources}
          sizes={sizes}
        />
      </TeaserImageBox>
      {hasContent && (
        <BackgroundCard component='section' className={cardClass} background={cardBackgroundColor}>
          {title && !showAsQuote && (
            <Title variant='h3' data-epi-edit='Title' regular>
              {title}
            </Title>
          )}
          <Typography
            variant='body1'
            component='section'
            data-epi-edit='Text'
            className='description'
          >
            <Html html={text} />
          </Typography>
          {quoteAuthor && showAsQuote && (
            <AuthorBox data-epi-edit='QuoteAuthor' className='author'>
              {quoteAuthor}
            </AuthorBox>
          )}
          {showAsQuote && <QuoteIcon className={imagePlacement} />}
        </BackgroundCard>
      )}
    </Box>
  );
  return (
    <TeaserBlockWrapper className={mainClass}>
      <BlockContainer>
        {sectionTitle && (
          <Title
            variant={sectionTitleSize || 'h2'}
            data-epi-edit='SectionTitle'
            sx={{
              textAlign: sectionTitleAlign ? sectionTitleAlign : 'left',
              marginBottom: {
                xs: 2,
                md: 0,
              },
            }}
          >
            {sectionTitle}
          </Title>
        )}
        <EditPanel
          disableEditPanel={disableEditPanel}
          properties={[
            'image',
            'imageConfig',
            'imagePlacement',
            'sectionTitle',
            'sectionTitleAlign',
            'sectionTitleSize',
            'title',
            'backgroundColor',
            'cardBackgroundColor',
            'link',
            'text',
            'showAsQuote',
            'quoteAuthor',
          ]}
        />
        {link && <SmartLink {...link}>{mainContent}</SmartLink>}
        {!link && mainContent}
      </BlockContainer>
    </TeaserBlockWrapper>
  );
};
